import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ModulePane } from '../components/ModulePane/ModulePane';
import { AccessNotice } from '../components/AccessNotice/AccessNotice';
import { isAccessDenied } from '../lib/dataverse';
import { useCurrentUser, useCan } from '../lib/identity';
import { fetchPolicyContext } from '../lib/datahub';
import {
  fetchActiveTemplate,
  fetchDemandsAndNeeds,
  fetchAddons,
  fetchAddonRationales,
  fetchBasisOptions,
  indexRationales,
  renderTemplate,
  saveSodan,
  type ProductDN,
  type BasisOption,
  type AddonDecisionValue,
  type AddonDecisionInput,
} from '../lib/compose';
import styles from './NewSodan.module.css';

function todayUK(): string {
  return new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

type AddonState = {
  addonref: string;
  name: string;
  decision: AddonDecisionValue;
  rationale: string;
  // remembers whether the rationale was hand-edited, so re-selecting a
  // decision only auto-fills when the broker hasn't customised it.
  edited: boolean;
};

export function NewSodan() {
  const user = useCurrentUser();
  const canCompose = useCan('compose');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepLinkPolicy = searchParams.get('policy') ?? '';

  // ---- step 1: pick a policy ----
  const [policyInput, setPolicyInput] = useState(deepLinkPolicy);
  const [activePolicyRef, setActivePolicyRef] = useState(deepLinkPolicy);

  useEffect(() => {
    if (deepLinkPolicy) setActivePolicyRef(deepLinkPolicy);
  }, [deepLinkPolicy]);

  const ctxQ = useQuery({
    queryKey: ['compose.policyContext', activePolicyRef],
    queryFn: () => fetchPolicyContext(activePolicyRef),
    enabled: !!activePolicyRef && !!user,
  });
  const ctx = ctxQ.data ?? null;
  const productType = ctx?.productType ?? '';

  // ---- step 2: load library for the product ----
  const libQ = useQuery({
    queryKey: ['compose.library', productType],
    queryFn: async () => {
      const [template, dn, addons, rationales, basis] = await Promise.all([
        fetchActiveTemplate('SODAN', productType),
        fetchDemandsAndNeeds(productType),
        fetchAddons(productType),
        fetchAddonRationales(),
        fetchBasisOptions(productType),
      ]);
      return { template, dn, addons, basis, rationaleIdx: indexRationales(rationales) };
    },
    enabled: !!productType,
  });

  // ---- editable SODAN state (seeded from the library once it loads) ----
  const [selectedDN, setSelectedDN] = useState<Record<string, boolean>>({});
  const [selectedBasis, setSelectedBasis] = useState<Record<string, boolean>>({});
  const [addonState, setAddonState] = useState<AddonState[]>([]);
  const [recommendation, setRecommendation] = useState('');
  const [justification, setJustification] = useState('');
  const [seededFor, setSeededFor] = useState<string>('');
  const [saveError, setSaveError] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // Seed defaults when the library for a NEW product arrives.
  useEffect(() => {
    if (!libQ.data || !productType || seededFor === productType) return;
    const dnDefaults: Record<string, boolean> = {};
    for (const d of libQ.data.dn) dnDefaults[d.gaqc_dnref] = true; // default all ticked
    setSelectedDN(dnDefaults);
    // Basis snippets start UNticked — choosing the basis is a deliberate
    // compliance decision, not a default.
    setSelectedBasis({});
    setAddonState(
      libQ.data.addons.map((a) => ({
        addonref: a.gaqc_addonref,
        name: a.gaqc_name ?? a.gaqc_addonref,
        decision: 'NA' as AddonDecisionValue,
        rationale: '',
        edited: false,
      }))
    );
    setRecommendation(
      ctx
        ? `We recommend ${ctx.productType} cover with ${ctx.insurer || 'the selected insurer'} as it meets the demands and needs identified above.`
        : ''
    );
    setJustification('');
    setSeededFor(productType);
  }, [libQ.data, productType, seededFor, ctx]);

  const rationaleIdx = libQ.data?.rationaleIdx ?? {};

  const setAddonDecision = (addonref: string, decision: AddonDecisionValue) => {
    setAddonState((prev) =>
      prev.map((a) => {
        if (a.addonref !== addonref) return a;
        // auto-fill rationale from library unless hand-edited
        let rationale = a.rationale;
        if (!a.edited) {
          if (decision === 'Taken') rationale = rationaleIdx[addonref]?.Take ?? '';
          else if (decision === 'Declined') rationale = rationaleIdx[addonref]?.Decline ?? '';
          else rationale = '';
        }
        return { ...a, decision, rationale };
      })
    );
  };

  const setAddonRationale = (addonref: string, rationale: string) => {
    setAddonState((prev) =>
      prev.map((a) => (a.addonref === addonref ? { ...a, rationale, edited: true } : a))
    );
  };

  // ---- live render ----
  const selectedDNList: ProductDN[] = useMemo(
    () => (libQ.data?.dn ?? []).filter((d) => selectedDN[d.gaqc_dnref]),
    [libQ.data, selectedDN]
  );
  const addonDecisions: AddonDecisionInput[] = useMemo(
    () =>
      addonState.map((a) => ({
        addonref: a.addonref,
        addonName: a.name,
        decision: a.decision,
        rationale: a.rationale,
      })),
    [addonState]
  );
  const selectedBasisList: BasisOption[] = useMemo(
    () => (libQ.data?.basis ?? []).filter((b) => selectedBasis[b.gaqc_basisref]),
    [libQ.data, selectedBasis]
  );
  // Group basis options by category for the picker, preserving fetch order
  // (product-specific first, then General; each already sorted by sortorder).
  const basisGroups: Array<[string, BasisOption[]]> = useMemo(() => {
    const order: string[] = [];
    const byCat = new Map<string, BasisOption[]>();
    for (const b of libQ.data?.basis ?? []) {
      const cat = b.gaqc_category || 'Other';
      if (!byCat.has(cat)) { byCat.set(cat, []); order.push(cat); }
      byCat.get(cat)!.push(b);
    }
    return order.map((c) => [c, byCat.get(c)!]);
  }, [libQ.data]);

  const rendered = useMemo(() => {
    if (!ctx || !libQ.data?.template?.gaqc_body) return '';
    return renderTemplate(libQ.data.template.gaqc_body, {
      ctx,
      brokerName: user?.displayName ?? '',
      today: todayUK(),
      selectedDN: selectedDNList,
      addonDecisions,
      recommendation,
      selectedBasis: selectedBasisList,
      justification,
    });
  }, [ctx, libQ.data, user, selectedDNList, addonDecisions, recommendation, selectedBasisList, justification]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rendered);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setSaveError('Copy failed — select the text and copy manually.');
    }
  };

  const handleSave = async () => {
    if (!ctx || !libQ.data?.template || !user) return;
    setSaving(true);
    setSaveError('');
    try {
      const res = await saveSodan({
        ctx,
        productType,
        selectedDNRefs: selectedDNList.map((d) => d.gaqc_dnref),
        selectedBasisRefs: selectedBasisList.map((b) => b.gaqc_basisref),
        recommendation,
        justification,
        addonDecisions,
        templateRef: libQ.data.template.gaqc_templateref,
        renderedBody: rendered,
        upn: user.upn,
      });
      navigate(`/c/${encodeURIComponent(res.correspondenceRef)}`);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed.');
      setSaving(false);
    }
  };

  if (!canCompose) {
    return (
      <ModulePane sidebar={<BackLink />} ariaLabel="New SODAN">
        <div className={styles.state}>
          You don’t have permission to compose SODAN drafts. Ask an admin for the <strong>user</strong> role.
        </div>
      </ModulePane>
    );
  }

  const sidebar = (
    <>
      <BackLink />
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="policyref">Policy / version ref</label>
        <input
          id="policyref"
          className={styles.search}
          placeholder="e.g. 174585686"
          value={policyInput}
          onChange={(e) => setPolicyInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setActivePolicyRef(policyInput.trim());
          }}
        />
        <button
          type="button"
          className={styles.loadBtn}
          onClick={() => setActivePolicyRef(policyInput.trim())}
          disabled={!policyInput.trim()}
        >
          Load policy
        </button>
        <p className={styles.hint}>
          Paste the policy&apos;s Version Ref from Acturis (we also accept the policy-year ref). Renewals &amp; Checklists can deep-link here with <code>?policy=</code>.
        </p>
      </div>

      {ctx && (
        <div className={styles.ctxCard}>
          <div className={styles.ctxName}>{ctx.clientName}</div>
          <dl className={styles.ctxList}>
            {ctx.clientAddress && <div><dt>Address</dt><dd>{ctx.clientAddress}</dd></div>}
            <div><dt>Product</dt><dd>{ctx.productType || '—'}</dd></div>
            <div><dt>Policy</dt><dd>{ctx.policyDescription || '—'}</dd></div>
            <div><dt>Insurer</dt><dd>{ctx.insurer || '—'}</dd></div>
            <div><dt>Premium</dt><dd>{ctx.premium || '—'}</dd></div>
          </dl>
        </div>
      )}
    </>
  );

  return (
    <ModulePane sidebar={sidebar} ariaLabel="New SODAN">
      <header className={styles.header}>
        <h1 className={styles.title}>New SODAN</h1>
        <p className={styles.lead}>
          Build a demands &amp; needs statement from the library, then copy it into Acturis.
        </p>
      </header>

      {!activePolicyRef && (
        <div className={styles.empty}>
          Enter a policy year ref in the sidebar and click <strong>Load policy</strong> to begin.
        </div>
      )}

      {activePolicyRef && ctxQ.isLoading && <div className={styles.state}>Loading policy from DataHub…</div>}
      {activePolicyRef && ctxQ.isSuccess && !ctx && (
        <div className={styles.empty}>
          No policy found for <code>{activePolicyRef}</code>. Check the policy year ref.
        </div>
      )}
      {ctxQ.isError &&
        (isAccessDenied(ctxQ.error) ? (
          <AccessNotice />
        ) : (
          <div className={styles.errorBox}>Couldn’t read DataHub. {String(ctxQ.error)}</div>
        ))}
      {libQ.isError &&
        (isAccessDenied(libQ.error) ? (
          <AccessNotice />
        ) : (
          <div className={styles.errorBox}>Couldn’t load the library. {String(libQ.error)}</div>
        ))}

      {ctx && libQ.isLoading && <div className={styles.state}>Loading {productType} library…</div>}

      {ctx && libQ.isSuccess && !libQ.data?.template && (
        <div className={styles.empty}>
          No active SODAN template for <strong>{productType || 'this product'}</strong> yet. An admin can add one in{' '}
          <Link to="/admin">Admin → Library</Link>. (The sample seed covers <strong>Commercial Motor</strong>.)
        </div>
      )}

      {ctx && libQ.isSuccess && libQ.data?.template && (
        <div className={styles.grid}>
          {/* ===== LEFT: the form ===== */}
          <div className={styles.formCol}>
            {/* Demands & needs */}
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Demands &amp; needs</h2>
              <p className={styles.cardHint}>Tick the demands and needs that apply to this client.</p>
              {libQ.data.dn.length === 0 && <p className={styles.muted}>No D&amp;N defined for this product.</p>}
              <ul className={styles.dnList}>
                {libQ.data.dn.map((d) => (
                  <li key={d.gaqc_dnref} className={styles.dnItem}>
                    <label className={styles.dnLabel}>
                      <input
                        type="checkbox"
                        checked={!!selectedDN[d.gaqc_dnref]}
                        onChange={(e) =>
                          setSelectedDN((prev) => ({ ...prev, [d.gaqc_dnref]: e.target.checked }))
                        }
                      />
                      <span>
                        <strong>{d.gaqc_name}</strong>
                        <span className={styles.dnText}>{d.gaqc_demandtext}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </section>

            {/* Add-ons */}
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Add-ons considered</h2>
              <p className={styles.cardHint}>
                For each add-on, record Taken / Declined / N/A. The standard rationale auto-fills and stays editable.
              </p>
              {addonState.length === 0 && <p className={styles.muted}>No add-ons defined for this product.</p>}
              {addonState.map((a) => (
                <div key={a.addonref} className={styles.addon}>
                  <div className={styles.addonHead}>
                    <span className={styles.addonName}>{a.name}</span>
                    <div className={styles.radioGroup} role="radiogroup" aria-label={`${a.name} decision`}>
                      {(['Taken', 'Declined', 'NA'] as AddonDecisionValue[]).map((opt) => (
                        <label
                          key={opt}
                          className={`${styles.radio} ${a.decision === opt ? styles.radioActive : ''}`}
                        >
                          <input
                            type="radio"
                            name={`addon-${a.addonref}`}
                            checked={a.decision === opt}
                            onChange={() => setAddonDecision(a.addonref, opt)}
                          />
                          {opt === 'NA' ? 'N/A' : opt}
                        </label>
                      ))}
                    </div>
                  </div>
                  {a.decision !== 'NA' && (
                    <textarea
                      className={styles.addonRationale}
                      value={a.rationale}
                      placeholder="Rationale…"
                      rows={2}
                      onChange={(e) => setAddonRationale(a.addonref, e.target.value)}
                    />
                  )}
                </div>
              ))}
            </section>

            {/* Recommendation */}
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Recommendation</h2>
              <textarea
                className={styles.bigText}
                rows={3}
                value={recommendation}
                onChange={(e) => setRecommendation(e.target.value)}
              />
            </section>

            {/* Basis of recommendation — pick standard reasons + optional free text */}
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Basis of recommendation</h2>
              <p className={styles.cardHint}>
                Tick the standard reasons that apply. They compose into the SODAN’s “Basis of our recommendation” section. Add anything bespoke in the box below.
              </p>
              {basisGroups.length === 0 && (
                <p className={styles.muted}>No standard basis reasons defined for this product.</p>
              )}
              {basisGroups.map(([category, options]) => (
                <div key={category} className={styles.basisGroup}>
                  <div className={styles.basisCategory}>{category}</div>
                  <ul className={styles.basisList}>
                    {options.map((b) => (
                      <li key={b.gaqc_basisref} className={styles.basisItem}>
                        <label className={styles.basisLabel}>
                          <input
                            type="checkbox"
                            checked={!!selectedBasis[b.gaqc_basisref]}
                            onChange={(e) =>
                              setSelectedBasis((prev) => ({ ...prev, [b.gaqc_basisref]: e.target.checked }))
                            }
                          />
                          <span>
                            <strong>{b.gaqc_name}</strong>
                            <span className={styles.basisText}>{b.gaqc_basistext}</span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <label className={styles.fieldLabel} style={{ marginTop: 12 }}>Additional bespoke basis (optional)</label>
              <textarea
                className={styles.bigText}
                rows={3}
                value={justification}
                placeholder="Anything specific to this client not covered above…"
                onChange={(e) => setJustification(e.target.value)}
              />
            </section>
          </div>

          {/* ===== RIGHT: live output ===== */}
          <div className={styles.outputCol}>
            <div className={styles.outputCard}>
              <div className={styles.outputHead}>
                <h2 className={styles.cardTitle}>SODAN output</h2>
                <div className={styles.outputActions}>
                  <button type="button" className={styles.copyBtn} onClick={handleCopy}>
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                  <button
                    type="button"
                    className={styles.saveBtn}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save draft'}
                  </button>
                </div>
              </div>
              {saveError && <div className={styles.errorBox}>{saveError}</div>}
              <pre className={styles.output}>{rendered}</pre>
            </div>
          </div>
        </div>
      )}
    </ModulePane>
  );
}

function BackLink() {
  return (
    <Link to="/" className={styles.back}>
      ← Back to drafts
    </Link>
  );
}
